import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutGrid, ShoppingCart, User, ChevronRight, LogIn, UserPlus, LogOut,
  LayoutDashboard, Receipt, Heart, Settings, Trash2,
  Building2, CreditCard, Users, BarChart3, Briefcase, Boxes, BookOpen,
  ShieldCheck, Cloud, Server, Globe, Sparkles,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cartStore, useCart, type CartItem } from "@/lib/cart";
import { useWishlist } from "@/lib/wishlist";
import { NotificationBell } from "@/components/site/notification-bell";

type AppItem = { label: string; to: string; icon: React.ComponentType<{ className?: string }>; color: string };

const APPS: AppItem[] = [
  { label: "ERP",         to: "/software",    icon: Building2,    color: "from-indigo-500 to-blue-500" },
  { label: "POS",         to: "/software",    icon: CreditCard,   color: "from-emerald-500 to-teal-500" },
  { label: "HRM",         to: "/software",    icon: Users,        color: "from-pink-500 to-rose-500" },
  { label: "Accounting",  to: "/software",    icon: BarChart3,    color: "from-amber-500 to-orange-500" },
  { label: "CRM",         to: "/software",    icon: Briefcase,    color: "from-violet-500 to-purple-500" },
  { label: "Inventory",   to: "/software",    icon: Boxes,        color: "from-cyan-500 to-sky-500" },
  { label: "School",      to: "/software",    icon: BookOpen,     color: "from-fuchsia-500 to-pink-500" },
  { label: "Hospital",    to: "/software",    icon: ShieldCheck,  color: "from-red-500 to-rose-500" },
  { label: "Hosting",     to: "/hosting",     icon: Cloud,        color: "from-sky-500 to-indigo-500" },
  { label: "VPS",         to: "/hosting",     icon: Server,       color: "from-slate-500 to-zinc-500" },
  { label: "Domains",     to: "/domains",     icon: Globe,        color: "from-teal-500 to-emerald-500" },
  { label: "AI Suite",    to: "/services",    icon: Sparkles,     color: "from-purple-500 to-fuchsia-500" },
];

type Open = null | "apps" | "cart" | "account";

export function HeaderUtilities() {
  const [open, setOpen] = useState<Open>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const { items: cart, count: cartCount, subtotal: cartTotal } = useCart();
  const wishlist = useWishlist();
  const wishCount = wishlist.length;

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(null);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (key: Exclude<Open, null>) => setOpen((o) => (o === key ? null : key));
  const close = () => setOpen(null);

  return (
    <div ref={wrapRef} className="relative flex items-center gap-1.5">
      {/* Apps launcher */}
      <button
        onClick={() => toggle("apps")}
        aria-label="Apps"
        aria-expanded={open === "apps"}
        className={`min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md p-2 text-muted-foreground hover:bg-secondary hover:text-foreground transition ${open === "apps" ? "bg-secondary text-foreground" : ""}`}
      >
        <LayoutGrid className="h-4 w-4" />
      </button>

      {/* Notifications */}
      <NotificationBell />

      {/* Wishlist / Favorites */}
      <Link
        to="/favorites"
        aria-label={`Favorites${wishCount > 0 ? ` (${wishCount})` : ""}`}
        className="relative min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md p-2 text-muted-foreground hover:bg-secondary hover:text-foreground transition"
      >
        <Heart className={`h-4 w-4 ${wishCount > 0 ? "fill-rose-500 text-rose-500" : ""}`} />
        {wishCount > 0 && (
          <span className="absolute 1 top-1 right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white shadow">
            {wishCount}
          </span>
        )}
      </Link>


      {/* Cart */}
      <button
        onClick={() => toggle("cart")}
        aria-label="Shopping cart"
        aria-expanded={open === "cart"}
        className={`relative min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md p-2 text-muted-foreground hover:bg-secondary hover:text-foreground transition ${open === "cart" ? "bg-secondary text-foreground" : ""}`}
      >
        <ShoppingCart className="h-4 w-4" />
        {cartCount > 0 && (
          <span className="absolute top-1 right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-gradient-brand px-1 text-[10px] font-bold leading-none text-white shadow-glow">
            {cartCount}
          </span>
        )}
      </button>

      {/* My Account */}
      <button
        onClick={() => toggle("account")}
        aria-label="My account"
        aria-expanded={open === "account"}
        className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-md px-2.5 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition ${open === "account" ? "bg-secondary text-foreground" : ""}`}
      >
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-brand text-white">
          <User className="h-3.5 w-3.5" />
        </span>
        <span className="hidden md:inline">My Account</span>
      </button>

      <AnimatePresence>
        {open === "apps" && <AppsPanel onNavigate={close} />}
        {open === "cart" && <CartPanel items={cart} total={cartTotal} onRemove={(id) => cartStore.remove(id)} onNavigate={close} />}
        {open === "account" && <AccountPanel onNavigate={close} />}
      </AnimatePresence>
    </div>
  );
}

function PanelShell({ children, width = "w-80" }: { children: React.ReactNode; width?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -6, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -6, scale: 0.98 }}
      transition={{ duration: 0.16 }}
      className={`absolute right-0 top-full z-50 mt-2 ${width} rounded-xl border border-border bg-card shadow-card glass overflow-hidden`}
    >
      {children}
    </motion.div>
  );
}

function AppsPanel({ onNavigate }: { onNavigate: () => void }) {
  return (
    <PanelShell width="w-[20rem]">
      <div className="border-b border-border px-4 py-3">
        <div className="text-sm font-semibold">CoderLity Apps</div>
        <div className="text-[11px] text-muted-foreground">Jump to any product or service</div>
      </div>
      <div className="grid grid-cols-3 gap-1 p-3">
        {APPS.map((a) => (
          <Link
            key={a.label}
            to={a.to}
            onClick={onNavigate}
            className="group flex flex-col items-center justify-center gap-1.5 rounded-lg p-3 hover:bg-secondary transition"
          >
            <span className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${a.color} text-white shadow-card group-hover:scale-105 transition`}>
              <a.icon className="h-5 w-5" />
            </span>
            <span className="text-[11px] font-medium text-foreground text-center leading-tight">{a.label}</span>
          </Link>
        ))}
      </div>
      <Link
        to="/marketplace"
        onClick={onNavigate}
        className="flex items-center justify-between border-t border-border px-4 py-2.5 text-xs font-semibold text-primary hover:bg-secondary transition"
      >
        View all apps in Marketplace
        <ChevronRight className="h-3.5 w-3.5" />
      </Link>
    </PanelShell>
  );
}

function CartPanel({
  items, total, onRemove, onNavigate,
}: {
  items: CartItem[];
  total: number;
  onRemove: (id: string) => void;
  onNavigate: () => void;
}) {
  return (
    <PanelShell width="w-80">
      <div className="border-b border-border px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold">Your cart</div>
          <div className="text-[11px] text-muted-foreground">{items.length} item{items.length !== 1 ? "s" : ""}</div>
        </div>
        <ShoppingCart className="h-4 w-4 text-muted-foreground" />
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <div className="mx-auto mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
            <ShoppingCart className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground">Your cart is empty</p>
          <Link to="/marketplace" onClick={onNavigate} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
            Browse marketplace <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      ) : (
        <>
          <ul className="max-h-72 overflow-y-auto divide-y divide-border">
            {items.map((it) => (
              <li key={it.id} className="flex items-start gap-3 px-4 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gradient-brand/10 text-primary ring-1 ring-border">
                  <Receipt className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{it.name}</div>
                  <div className="text-xs text-muted-foreground">Qty {it.qty} · ${it.price.toFixed(2)}</div>
                </div>
                <button
                  onClick={() => onRemove(it.id)}
                  className="rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-destructive transition"
                  aria-label={`Remove ${it.name}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-border p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-bold text-gradient">${total.toFixed(2)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Link to="/cart" onClick={onNavigate} className="rounded-md border border-border px-3 py-2 text-center text-xs font-semibold hover:bg-secondary transition">
                View cart
              </Link>
              <Link to="/cart" onClick={onNavigate} className="rounded-md bg-gradient-brand px-3 py-2 text-center text-xs font-semibold text-white shadow-glow hover:opacity-95 transition">
                Checkout
              </Link>
            </div>
          </div>
        </>
      )}
    </PanelShell>
  );
}

function AccountPanel({ onNavigate }: { onNavigate: () => void }) {
  const { user } = useAuth();
  const links = [
    { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
    { label: "Profile",   to: "/profile",   icon: Receipt },
    { label: "Billing",   to: "/billing",   icon: Heart },
    { label: "Settings",  to: "/security",  icon: Settings },
  ];

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Signed out");
    onNavigate();
  };

  return (
    <PanelShell width="w-72">
      <div className="border-b border-border bg-hero-glow px-4 py-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-brand text-white shadow-glow">
            <User className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">
              {user ? `Welcome${user.email ? `, ${user.email}` : ""}` : "Welcome, Guest"}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {user ? "You're signed in" : "Sign in to access your dashboard"}
            </div>
          </div>
        </div>
        {!user && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link to="/login" onClick={onNavigate} className="inline-flex items-center justify-center gap-1 rounded-md bg-gradient-brand px-3 py-2 text-xs font-semibold text-white shadow-glow hover:opacity-95 transition">
              <LogIn className="h-3.5 w-3.5" /> Sign in
            </Link>
            <Link to="/signup" onClick={onNavigate} className="inline-flex items-center justify-center gap-1 rounded-md border border-border px-3 py-2 text-xs font-semibold hover:bg-secondary transition">
              <UserPlus className="h-3.5 w-3.5" /> Sign up
            </Link>
          </div>
        )}
      </div>
      <ul className="py-1">
        {links.map((l) => (
          <li key={l.label}>
            <Link to={l.to} onClick={onNavigate} className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-secondary transition">
              <l.icon className="h-4 w-4 text-muted-foreground" />
              <span>{l.label}</span>
              <ChevronRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
            </Link>
          </li>
        ))}
      </ul>
      {user && (
        <button
          onClick={handleSignOut}
          className="flex w-full items-center gap-3 border-t border-border px-4 py-2.5 text-sm text-muted-foreground hover:bg-secondary hover:text-destructive transition"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      )}
    </PanelShell>
  );
}
